import {
  EmptyResponse,
  LoginResponse,
  PasskeyAssertion,
  StaffElevationResponse,
  StaffPasskeyBeginResponse,
  TokenRefreshResponse,
  TypedApiRequestParams,
  UserDetail,
} from '../types';

const login = (
  email: string,
  password: string,
): TypedApiRequestParams<LoginResponse> => ({
  url: 'auth/signin/',
  method: 'POST',
  params: {email, password},
  authRequired: false,
});

const facebookLogin = (
  accessToken: string,
): TypedApiRequestParams<LoginResponse> => ({
  url: 'auth/facebook/signin/',
  method: 'POST',
  params: {access_token: accessToken},
  authRequired: false,
});

// Native Google sign-in hands us a verifiable OIDC credential; the backend
// checks its signature and audience itself. The auth-code flow it replaces
// required offlineAccess + forceCodeForRefreshToken, which re-ran Google's
// consent grant on every sign-in for a refresh token nothing ever read.
const googleLogin = (
  idToken: string,
): TypedApiRequestParams<LoginResponse> => ({
  url: 'auth/google/signin/',
  method: 'POST',
  params: {id_token: idToken},
  authRequired: false,
});

const googleWebLogin = (
  code: string,
): TypedApiRequestParams<LoginResponse> => ({
  url: 'auth/google-web/signin/',
  method: 'POST',
  params: {code},
  authRequired: false,
});

const appleLogin = (
  authorizationCode: string,
  identityToken: string,
): TypedApiRequestParams<LoginResponse> => ({
  url: 'auth/apple/signin/',
  method: 'POST',
  params: {access_token: authorizationCode, id_token: identityToken},
  authRequired: false,
});

const refreshToken = (
  refresh: string,
): TypedApiRequestParams<TokenRefreshResponse> => ({
  url: 'auth/token/refresh/',
  method: 'POST',
  params: {refresh},
  authRequired: false,
});

const register = (
  email: string,
  firstName: string,
  lastName: string,
  password1: string,
  password2: string,
): TypedApiRequestParams<LoginResponse> => ({
  url: 'auth/signup/',
  method: 'POST',
  params: {
    first_name: firstName,
    last_name: lastName,
    email,
    password1,
    password2,
  },
  authRequired: false,
});

const verifyEmail = (key: string): TypedApiRequestParams<EmptyResponse> => ({
  url: 'auth/signup/verify-email/',
  method: 'POST',
  params: {key},
  authRequired: false,
});

const passwordReset = (
  email: string,
): TypedApiRequestParams<EmptyResponse> => ({
  url: 'auth/password/reset/',
  method: 'POST',
  params: {email},
  authRequired: false,
});

const passwordResetConfirm = (
  token: string,
  uid: string,
  password1: string,
  password2: string,
): TypedApiRequestParams<EmptyResponse> => ({
  url: 'auth/password/reset/confirm/',
  method: 'POST',
  params: {
    token,
    uid,
    password1,
    password2,
  },
  authRequired: false,
});

const passwordChange = (
  oldPassword: string,
  password1: string,
  password2: string,
): TypedApiRequestParams<EmptyResponse> => ({
  url: 'auth/password/change/',
  method: 'POST',
  params: {
    old_password: oldPassword,
    password1,
    password2,
  },
  authRequired: true,
});

const facebookConnect = (
  accessToken: string,
): TypedApiRequestParams<UserDetail> => ({
  url: 'auth/facebook/connect/',
  method: 'POST',
  params: {access_token: accessToken},
  authRequired: true,
});

const googleConnect = (idToken: string): TypedApiRequestParams<UserDetail> => ({
  url: 'auth/google/connect/',
  method: 'POST',
  params: {id_token: idToken},
  authRequired: true,
});

const googleWebConnect = (code: string): TypedApiRequestParams<UserDetail> => ({
  url: 'auth/google-web/connect/',
  method: 'POST',
  params: {code},
  authRequired: true,
});

const appleConnect = (
  authorizationCode: string,
  identityToken: string,
): TypedApiRequestParams<UserDetail> => ({
  url: 'auth/apple/connect/',
  method: 'POST',
  params: {access_token: authorizationCode, id_token: identityToken},
  authRequired: true,
});

const emailChange = (
  newEmail: string,
  password: string,
): TypedApiRequestParams<EmptyResponse> => ({
  url: 'auth/email/change/',
  method: 'POST',
  params: {new_email: newEmail, password},
  authRequired: true,
});

const emailChangeConfirm = (
  key: string,
): TypedApiRequestParams<{email: string}> => ({
  url: 'auth/email/change/confirm/',
  method: 'POST',
  params: {key},
  authRequired: false,
});

/**
 * Staff second factor: trade a TOTP or recovery code for the short-lived token
 * the backend requires (as `X-Staff-Elevation`) on Login As and user search.
 * Send it with `skipLoginAs` -- under an `X-Login-As` header the backend would
 * authenticate the impersonated user instead of the admin.
 */
const elevate = (
  code: string,
): TypedApiRequestParams<StaffElevationResponse> => ({
  url: 'auth/mfa/elevate/',
  method: 'POST',
  params: {code},
  authRequired: true,
});

/**
 * The same elevation from a passkey, in two steps: `begin` returns WebAuthn
 * request options for the OS (the challenge is single-use and short-lived),
 * `complete` posts back the OS's assertion. Both with `skipLoginAs`, as
 * `elevate`.
 */
const elevatePasskeyBegin =
  (): TypedApiRequestParams<StaffPasskeyBeginResponse> => ({
    url: 'auth/mfa/elevate/passkey/begin/',
    method: 'POST',
    params: {},
    authRequired: true,
  });

const elevatePasskeyComplete = (
  credential: PasskeyAssertion,
): TypedApiRequestParams<StaffElevationResponse> => ({
  url: 'auth/mfa/elevate/passkey/complete/',
  method: 'POST',
  params: {credential},
  authRequired: true,
});

export default {
  login,
  register,
  verifyEmail,
  facebookLogin,
  googleLogin,
  googleWebLogin,
  appleLogin,
  refreshToken,
  passwordReset,
  passwordResetConfirm,
  passwordChange,
  facebookConnect,
  googleConnect,
  googleWebConnect,
  appleConnect,
  emailChange,
  emailChangeConfirm,
  elevate,
  elevatePasskeyBegin,
  elevatePasskeyComplete,
};
